<script setup lang="ts">
/**
 * Le message de test d'un destinataire (gestes 35 et 36, route 6, puis la
 * relecture de la route 6 bis ; écran 6, `rendreTest` S:6964-7000).
 *
 * Ce que l'utilisateur doit vivre : sur la ligne d'une personne prévenue, il
 * s'envoie (ou envoie à un collègue) un vrai message de test. Un dialogue dit
 * d'abord à qui part le message et par quel canal, et rien ne part sans sa
 * confirmation. Après l'envoi (202), le résultat est relu une fois tout de
 * suite, puis deux fois, espacées, dans la minute ; rien de plus, et rien
 * après le départ de la page. Quand on ne sait pas si le message est parti,
 * l'écran le dit et relit le résultat avant de laisser en envoyer un autre.
 * Un employé ne voit jamais un chiffre du numéro.
 */
// ================================
// IMPORTS
// ================================
import { computed, onBeforeUnmount, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import {
    cibleTest,
    useActionsStore,
} from "@modules/alarmes-v2/stores/actions.store"
import { useResultatsTestStore } from "@modules/alarmes-v2/stores/resultatsTest.store"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import type {
    AlarmesV2Canal,
    AlarmesV2EnvoiTest,
} from "@modules/alarmes-v2/types/alarmes-v2"
import type { LignePrevenu } from "@modules/alarmes-v2/utils/prevenus"
import { phraseDuRefus } from "@modules/alarmes-v2/utils/ecritures"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** La surveillance. */
    idInstance: string
    /** La ligne du destinataire (nom, canaux, état du numéro). */
    ligne: LignePrevenu
    /** Le masque du numéro, si la matrice permet de le voir ; `""` sinon. */
    masque: string
}>()

/**
 * Les deux relectures espacées après un envoi, en millisecondes depuis
 * l'envoi. [PROPOSITION] sur les délais.
 */
const RELECTURES_APRES_ENVOI_MS = [15000, 45000] as const

const auth = useAuthStore()
const actions = useActionsStore()
const resultats = useResultatsTestStore()
const { acces } = useAcces()

const ouvert = ref(false)
const canal = ref<AlarmesV2Canal>("sms")
let minuteurs: ReturnType<typeof setTimeout>[] = []

// ================================
// COMPUTED
// ================================
const getCible = computed(() => cibleTest(props.idInstance, props.ligne.idUser))
const getEtat = computed(() => actions.etat(getCible.value))
const getOccupee = computed(() => actions.occupee(getCible.value))

/** Les canaux par lesquels un test peut partir vers cette personne. */
const getCanaux = computed((): AlarmesV2Canal[] => {
    const c: AlarmesV2Canal[] = []
    if (props.ligne.sms && props.ligne.etatNumero === "renseigne") c.push("sms")
    if (props.ligne.mail) c.push("mail")
    return c
})

/** Soi-même ou un collègue : deux gestes de la matrice. */
const getAcces = computed(() =>
    acces(
        props.ligne.idUser === auth.userId
            ? "test_pour_soi"
            : "test_pour_autrui"
    )
)

const getNomCanal = (c: AlarmesV2Canal) =>
    c === "sms"
        ? t("alarmesV2.actions.test.sms")
        : t("alarmesV2.actions.test.mail")

/** La phrase d'un refus dans le dialogue. */
const getRefus = computed((): string => {
    const e = getEtat.value
    if (!e || e.statut !== "refus" || !e.erreur) return ""
    const p = phraseDuRefus(e.erreur)
    return p.texte ?? t(p.cle as string)
})

const getTexteFait = computed((): string => {
    const e = getEtat.value
    if (!e || e.statut !== "fait") return ""
    const r = (e.valeur ?? {}) as AlarmesV2EnvoiTest
    const parti = r.canal ?? canal.value
    const fait = t("alarmesV2.actions.fait.test", {
        nom: props.ligne.nom,
        canal: getNomCanal(parti),
    })
    return `${fait} ${t("alarmesV2.actions.test.relectures")}`
})

// ================================
// HANDLERS
// ================================
/** Relit le résultat : UNE lecture de la route 6 bis. */
async function relireLeResultat(): Promise<boolean> {
    await resultats.relire(props.idInstance, props.ligne.idUser)
    return (
        resultats.resultat(props.idInstance, props.ligne.idUser)?.statut ===
        "pret"
    )
}

function arreterLesMinuteurs(): void {
    minuteurs.forEach((m) => clearTimeout(m))
    minuteurs = []
}

function onOuvrir(): void {
    if (getOccupee.value || getCanaux.value.length === 0) return
    actions.oublier(getCible.value)
    canal.value = getCanaux.value[0]
    ouvert.value = true
}

function onFermer(): void {
    if (getOccupee.value) return
    ouvert.value = false
}

/** Envoie : UNE requête ; puis une lecture du résultat, et deux espacées. */
async function onEnvoyer(): Promise<void> {
    if (getOccupee.value || !getCanaux.value.includes(canal.value)) return
    const idInstance = props.idInstance
    const idUser = props.ligne.idUser
    const choisi = canal.value
    const session = cleDeSession()
    const issue = await actions.ecrire({
        cible: cibleTest(idInstance, idUser),
        geste: "test",
        appel: () =>
            serviceAlarmesV2.alarmesV2EnvoyerTest(idInstance, idUser, choisi),
        relire: relireLeResultat,
    })
    if (issue.issue === "refus" || issue.issue === "ignoree") return
    ouvert.value = false
    if (issue.issue !== "fait") return
    arreterLesMinuteurs()
    minuteurs = RELECTURES_APRES_ENVOI_MS.map((ms) =>
        setTimeout(() => {
            // Une autre session : rien n'est relu pour elle.
            if (cleDeSession() !== session) return
            void resultats.relire(idInstance, idUser)
        }, ms)
    )
}

// ================================
// LIFECYCLE
// ================================
onBeforeUnmount(arreterLesMinuteurs)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <span
        v-if="getCanaux.length > 0 || getEtat"
        class="mt-1 flex flex-col items-start gap-1"
        data-test="panneau-test"
    >
        <BoutonGeste
            v-if="getCanaux.length > 0"
            :acces="getAcces"
            :libelle="t('alarmesV2.actions.test.bouton')"
            classe="rounded border border-surface-300 px-2 py-1 text-xs font-medium text-surface-800"
            @click="onOuvrir"
        />
        <AnnonceEcriture
            v-if="getEtat && !ouvert"
            :cible="getCible"
            :texte-fait="getTexteFait"
            :texte-incertain="t('alarmesV2.actions.test.incertain')"
            :relire="relireLeResultat"
        />

        <CadreDialogue
            v-if="ouvert"
            :titre="t('alarmesV2.actions.test.titre')"
            :bloque="getOccupee"
            @fermer="onFermer"
        >
            <fieldset v-if="getCanaux.length > 1" class="flex flex-col gap-1">
                <legend class="text-sm font-semibold">
                    {{ t("alarmesV2.actions.test.canal") }}
                </legend>
                <label
                    v-for="c in getCanaux"
                    :key="c"
                    class="flex items-center gap-2 text-sm"
                >
                    <input
                        v-model="canal"
                        type="radio"
                        :value="c"
                        :data-test="`canal-${c}`"
                    />
                    {{ getNomCanal(c) }}
                </label>
            </fieldset>
            <p class="text-sm" data-test="test-vers">
                {{
                    t("alarmesV2.actions.test.vers", {
                        nom: ligne.nom,
                        canal: getNomCanal(canal),
                    })
                }}
                <template v-if="canal === 'sms'">
                    {{
                        masque
                            ? t("alarmesV2.actions.test.auNumero", { masque })
                            : t("alarmesV2.actions.test.numeroRenseigne")
                    }}
                    {{ t("alarmesV2.actions.test.unSms") }}
                </template>
            </p>
            <p class="text-xs text-surface-600">
                {{ t("alarmesV2.actions.test.facultatif") }}
            </p>
            <p v-if="getOccupee" class="text-sm text-surface-700" role="status">
                {{ t("alarmesV2.actions.envoi") }}
            </p>
            <p
                v-else-if="getRefus"
                class="text-sm text-red-800"
                role="alert"
                data-test="refus"
            >
                {{ getRefus }}
            </p>
            <template #pied>
                <button
                    type="button"
                    class="rounded border border-surface-300 px-3 py-1.5 text-sm"
                    :aria-disabled="getOccupee ? 'true' : undefined"
                    data-focus-initial
                    data-test="annuler"
                    @click="onFermer"
                >
                    {{ t("alarmesV2.actions.annuler") }}
                </button>
                <button
                    type="button"
                    class="rounded border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white"
                    :aria-disabled="getOccupee ? 'true' : undefined"
                    data-test="envoyer"
                    @click="onEnvoyer"
                >
                    {{ t("alarmesV2.actions.test.envoyer") }}
                </button>
            </template>
        </CadreDialogue>
    </span>
</template>
